import { v4 as uuidv4 } from "uuid";
import { tiles } from "common/model/tiles";
import { MapModel, AssetModelType } from "common/model";
import { GeneratedMapData } from "features/builder/api";
import { findBuilderAsset } from "features/builder/assets";
import { MAP_MESH_SIZE } from "features/builder/constants";

/**
 * AI生成マップ（グリッド座標）をビルダーのマップモデル（ワールド座標）へ変換する。
 *
 * 「これを つかう」での適用とプレビュー描画で必ず同じ結果になるよう、両方からこの関数を使う。
 */
export const generatedMapToModels = (data: GeneratedMapData): MapModel[] => {
  const models: MapModel[] = [];

  // タイル名からTileSetを取得するヘルパー
  const getTileByName = (name: string) => tiles.find((t) => t.name === name);

  // タイル名から高さを取得するヘルパー
  const getTileHeight = (name: string): number => {
    if (name.startsWith("tileLow")) return 1;
    if (name.startsWith("tileMedium")) return 1.5;
    if (name.startsWith("tileHigh")) return 2;
    return 1;
  };

  // 地面高さマップを作成（オブジェクト配置用）
  const groundHeightMap: Record<string, number> = {};

  // グリッド中心点オフセット
  const offset = MAP_MESH_SIZE / 2;

  // 地面タイルを追加（y: 0 に配置）
  for (const tile of data.tiles) {
    const tileSet = getTileByName(tile.name);
    if (tileSet) {
      const height = getTileHeight(tile.name);
      const key = `${tile.x}_${tile.z}`;
      groundHeightMap[key] = height;

      models.push({
        id: uuidv4(),
        name: tileSet.name,
        filename: tileSet.filename,
        type: tileSet.type,
        position: {
          x: tile.x * MAP_MESH_SIZE + offset,
          y: 0,
          z: tile.z * MAP_MESH_SIZE + offset,
        },
      });
    }
  }

  // 指定座標の地面高さを取得するヘルパー
  const getGroundHeight = (x: number, z: number): number => {
    const key = `${x}_${z}`;
    return groundHeightMap[key] ?? 1;
  };

  // キャラクター（地面の上に配置）
  // offset / scaling / rotation は common/model/tiles ではなくビルダーのアセット定義に合わせる。
  // tiles 側はコース用で offset.y と z 反転が異なり、そのまま使うと宙に浮く
  const characterTile = findBuilderAsset("character_duck") ?? getTileByName("character_duck");
  if (characterTile) {
    const groundHeight = getGroundHeight(data.character.x, data.character.z);
    models.push({
      id: uuidv4(),
      name: characterTile.name,
      filename: characterTile.filename,
      type: AssetModelType.CHARACTER,
      position: {
        x: data.character.x * MAP_MESH_SIZE + offset,
        y: groundHeight,
        z: data.character.z * MAP_MESH_SIZE + offset,
      },
      rotation: characterTile.rotation,
      offset: characterTile.offset,
      scaling: characterTile.scaling,
    });
  }

  // ゴール（地面の上に配置）
  const goalTile = getTileByName("flag_teamRed");
  if (goalTile) {
    const groundHeight = getGroundHeight(data.goal.x, data.goal.z);
    models.push({
      id: uuidv4(),
      name: goalTile.name,
      filename: goalTile.filename,
      type: AssetModelType.GOAL,
      position: {
        x: data.goal.x * MAP_MESH_SIZE + offset,
        y: groundHeight,
        z: data.goal.z * MAP_MESH_SIZE + offset,
      },
    });
  }

  // 宝石（地面の上に配置）
  for (const gem of data.gems || []) {
    const gemTile = getTileByName("diamond_teamRed");
    if (gemTile) {
      const groundHeight = getGroundHeight(gem.x, gem.z);
      models.push({
        id: uuidv4(),
        name: gemTile.name,
        filename: gemTile.filename,
        type: AssetModelType.GEM,
        position: {
          x: gem.x * MAP_MESH_SIZE + offset,
          y: groundHeight,
          z: gem.z * MAP_MESH_SIZE + offset,
        },
      });
    }
  }

  // 装飾（地面の上に配置）
  for (const deco of data.decorations || []) {
    const decoTile = getTileByName(deco.name);
    if (decoTile) {
      const groundHeight = getGroundHeight(deco.x, deco.z);
      models.push({
        id: uuidv4(),
        name: decoTile.name,
        filename: decoTile.filename,
        type: AssetModelType.DECORATION,
        position: {
          x: deco.x * MAP_MESH_SIZE + offset,
          y: groundHeight,
          z: deco.z * MAP_MESH_SIZE + offset,
        },
      });
    }
  }

  return models;
};
